import { Button, Card, Input, Modal, Popconfirm, Select, Tooltip } from "antd";
import { api } from "../../../../config";
import "../../../Calendar/calendar.css";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { AuthenticationState } from "../../../../interfaces/users";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import { TicketViewModel } from "../../../../interfaces";
import { Image } from "antd";
import Title from "antd/es/typography/Title";
import SplitTicket from "../../../Shared/Components/SplitTicket";
import { useQuery } from "react-query";
import TextArea from "antd/es/input/TextArea";
import { removeAllTicketEvents, scrollToElement } from "../../../../utils";
import { PartsRequestList } from "./partsRequestList";
import Link from "antd/es/typography/Link";

dayjs.extend(duration);

interface AttachmentsAnalysisProps {
  id: number;
  refetch?: any;
  ticket?: TicketViewModel;
}

interface EventFormProps {
  description: string;
  start?: Date;
  end?: Date;
  notes?: string;
  ticketId?: number;
  agendaId?: number;
}

const defaultForm: EventFormProps = {
  description: "",
  start: undefined,
  end: undefined,
  notes: "",
  ticketId: undefined,
  agendaId: undefined,
};

export const AttachmentsAnalysis: React.FC<AttachmentsAnalysisProps> = ({
  id,
  refetch,
  ticket,
}) => {
  const { token, user } = useSelector((state: AuthenticationState) => state);
  const { refetch: boardRefetch } = useQuery({
    queryKey: `tickets/assemble/board`,
  });
  const [okLoading] = useState<any>(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [categories, setCategories] = useState<any[]>();
  const [products, setProducts] = useState<any[]>();
  const [showSplitTicketModal, setShowSplitTicketModal] = useState(false);

  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  const [loadImages, setLoadImages] = useState<{ [key: string]: boolean }>({});

  const [rejectLoading, setRejectLoading] = useState(false);
  const [rejectSelect, setRejectSelect] = useState("ASSEMBLE_REJECTED");
  const [motive, setMotive] = useState<string>("");
  const [quilometragem, setQuilometragem] = useState<any>(0);
  const [sendToScheduleComment, setSendToScheduleComment] =
    useState<string>("");

  const [paymentAdditionals, setPaymentAdditionals] = useState<any[]>([]);
  const relatedTicket = ticket as any;
  const childFieldReports = relatedTicket?.childFieldReports ?? [];

  useEffect(() => {
    if (!isModalOpen) return;

    api(token)
      .get(`/product-image-category`)
      .then((response) => {
        setCategories(response);
      });

    api(token)
      .get(`tickets/${id}/products/attachments`)
      .then((response) => {
        setProducts(response);
      });

    api(token)
      .get(`payment/additions?fieldReportId=${id}`)
      .then((response) => {
        const additionals = response.filter(
          (addition: any) => addition.type.name === "QUILOMETRAGEM"
        );
        setPaymentAdditionals(additionals);
        setQuilometragem(additionals[0]?.value);
      });
  }, [id, isModalOpen, token]);

  const handleClose = () => {
    setIsModalOpen(false);
    setLoadImages({});
  };

  const approveTicket = () => {
    api(token)
      .put(`ticket/assemble/${id}`, { status: "ASSEMBLE_APPROVED" })
      .then(() => {
        toast.success("Ticket aprovado com sucesso!");
        handleClose();
        refetch();
        boardRefetch();
      })
      .catch((error) => {
        toast.error(error.response.data.message);
      });
  };

  const rejectTicket = async () => {
    setRejectLoading(true);

    try {
      await api(token).put(`ticket/assemble/${id}`, {
        status: rejectSelect,
        statusDescription: motive,
        cancellationReason: null,

        ...(ticket?.cancellationImages
          ? {
              cancellationImages: {
                delete: ticket.cancellationImages.map((image: any) => ({
                  id: image.id,
                })),
              },
            }
          : {}),
      });

      await api(token).post(`comments`, {
        content: `Motivo da rejeição: ${motive}`,
        fieldReportId: id,
        userId: user?.id,
      });
      toast.success("Ticket rejeitado com sucesso!");
      setShowConfirmationModal(false);
      handleClose();
      refetch?.();
      boardRefetch();
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ??
          error?.response?.data?.error ??
          "Ocorreu um erro ao rejeitar o ticket"
      );
    } finally {
      setRejectLoading(false);
    }
  };

  // const updateQuilometragem = () => {
  //   api(token)
  //     .put(`payment/additions/${paymentAdditionals[0].id}`, {
  //       value: Number(quilometragem),
  //     })
  //     .then(() => {
  //       toast.success("Quilometragem salva com sucesso!");
  //     })
  //     .catch((error) => {
  //       console.log(error);
  //       toast.error("Ocorreu um erro ao salvar a quilometragem");
  //     });
  // };

  const sendToSchedule = () => {
    api(token)
      .put(`ticket/assemble/${id}`, {
        status: "ASSEMBLE_PENDING_SCHEDULE",
        ownerId: null,
        cancellationReason: null,
        cancellationImages: {
          ...(ticket?.cancellationImages
            ? {
                delete: ticket.cancellationImages.map((image: any) => ({
                  id: image.id,
                })),
              }
            : {}),
        },
      })
      .then(async () => {
        await api(token).post(`comments`, {
          content: `Motivo do reagendamento: ${sendToScheduleComment}`,
          fieldReportId: id,
          userId: user?.id,
        });
        toast.success("Ticket enviado para agendendamento com sucesso!");
        handleClose();
        refetch();
        removeAllTicketEvents(id, token);
        boardRefetch();
      })
      .catch((error) => {
        toast.error(error.response.data.message);
      });
  };

  const clientCategoryId = useMemo(
    () => categories?.find((category) => category.name.match(/cliente/gi))?.id,
    [categories]
  );

  const videosCategoriesIds = useMemo(
    () =>
      categories
        ?.filter((category) => category.productImageFormatType === "VIDEO")
        .map((category) => category.id),
    [categories]
  );

  const getClientImage = useCallback(
    (
      fieldReportAttachments: TicketViewModel["groups"][0]["fieldReportProductAttachment"]
    ) => {
      return fieldReportAttachments.find(
        (attachment) =>
          !attachment.fieldReportProductId &&
          attachment.productImageCategoryId === clientCategoryId &&
          !videosCategoriesIds?.includes(attachment.productImageCategoryId)
      );
    },
    [clientCategoryId, videosCategoriesIds]
  );

  const getImagesWithoutClient = useCallback(
    (
      fieldReportAttachments: TicketViewModel["groups"][0]["fieldReportProductAttachment"]
    ) => {
      return fieldReportAttachments.filter(
        (attachment) =>
          !attachment.fieldReportProductId &&
          attachment.productImageCategoryId !== clientCategoryId &&
          !videosCategoriesIds?.includes(attachment.productImageCategoryId)
      );
    },
    [clientCategoryId, videosCategoriesIds]
  );

  const getVideos = useCallback(
    (
      fieldReportAttachments: TicketViewModel["groups"][0]["fieldReportProductAttachment"]
    ) =>
      fieldReportAttachments.filter((attachment) =>
        videosCategoriesIds?.includes(attachment.productImageCategoryId)
      ),
    [videosCategoriesIds]
  );

  const getProductImageByCategory = (product: any, category: string) => {
    return product.productAttachment.find((attachment: any) => {
      return attachment.productImageCategoryId === category;
    });
  };

  return (
    <>
      {ticket && (
        <Modal
          open={showSplitTicketModal}
          onCancel={() => setShowSplitTicketModal(false)}
          centered
          width="50%"
          footer={null}
        >
          <SplitTicket ticket={ticket} />
        </Modal>
      )}
      {ticket && (
        <Modal
          open={showConfirmationModal}
          onCancel={() => setShowConfirmationModal(false)}
          centered
          zIndex={5000}
          width="15%"
          title="Rejeitar Ticket"
          footer={null}
        >
          <div>
            <p>
              Tem certeza que deseja <b>rejeitar o ticket por completo?</b>
            </p>
            <div className="mb-4 flex gap-2 flex-col">
              <Select
                className="w-full"
                onChange={setRejectSelect}
                defaultValue={rejectSelect}
              >
                <Select.Option value="ASSEMBLE_REJECTED">
                  Rejeitado
                </Select.Option>
                <Select.Option value="ASSEMBLE_TECHNICAL_ASSISTANCE">
                  Assistencia Técnica
                </Select.Option>
              </Select>

              <TextArea
                value={motive}
                onChange={(e) => setMotive(e.target.value)}
                size="small"
                placeholder="Digite um motivo"
                rows={2}
              />
            </div>
            <div className="flex justify-between">
              <Button onClick={() => setShowConfirmationModal(false)}>
                Não
              </Button>
              {/* <Button
                onClick={() => {
                  setShowConfirmationModal(false);
                  setShowSplitTicketModal(true);
                }}
                type="dashed"
              >
                Gerar Parcial{" "}
              </Button> */}
              <Tooltip title={motive === "" ? "Digite um motivo" : ""}>
                <Button
                  onClick={() => {
                    rejectTicket();
                  }}
                  type="primary"
                  loading={rejectLoading}
                  disabled={motive === "" || rejectLoading}
                >
                  Sim
                </Button>
              </Tooltip>
            </div>
          </div>
        </Modal>
      )}
      <Modal
        width={1000}
        title="Analise"
        open={isModalOpen}
        footer={null}
        confirmLoading={okLoading}
        onCancel={handleClose}
        bodyStyle={{ maxHeight: "75vh", overflowY: "auto" }}
      >
        <>
          {!!childFieldReports.length && (
            <Card
              title="Tickets Filho"
              size="small"
              headStyle={{ color: " rgb(59 130 246)" }}
              className="sticky top-0 z-20 mb-5 bg-white shadow-sm"
            >
              <div className="flex flex-wrap gap-x-4 gap-y-2">
                {childFieldReports.map((item: any) => (
                  <Link key={item.id} href={`/montagem?id=${item.id}`}>
                    {item.title}
                  </Link>
                ))}
              </div>
            </Card>
          )}
          {ticket?.cancellationReason ? (
            <>
              <div className="w-full flex items-center flex-col gap-5 my-5">
                <div className="bg-red-500 w-fit text-white px-5 py-1">
                  <b>Atenção:</b>O montador solicitou o Reagendamento/Cancelamento deste Ticket
                </div>
              </div>

              <div>
                <div>
                  <b>Motivo relatado:</b>
                </div>
                <div>{ticket.cancellationReason}</div>
              </div>
              <div className="w-full flex flex-col items-center gap-4">
                {ticket.cancellationImages?.map((image) => (
                  <Image
                    key={image.id}
                    className="max-h-[500px]"
                    src={image.url}
                  />
                ))}
              </div>
            </>
          ) : (
            <>
              {products
                ?.filter((product) => !ticket?.groups.length ? true : !product.productGroupId)
                .map((product: any) => {
                  return (
                    <div
                      key={product.id}
                      className="flex flex-col w-full justify-center align-center"
                    >
                      <div className="mx-auto  flex flex-col items-center">
                        <Title level={5}> {product.product.description}</Title>
                      </div>

                      {categories && (
                        <div
                          key={`key-${product.id}-${clientCategoryId}`}
                          className="flex flex-col  w-full justify-center align-center"
                        >
                          <div
                            id={`${product.id}-${clientCategoryId}`}
                            className="mx-auto my-5 gap-2 flex flex-col items-center"
                          >
                            <Image
                              className="max-h-[500px]"
                              src={
                                getProductImageByCategory(
                                  product,
                                  clientCategoryId
                                )?.attachment?.url
                              }
                            />
                            {
                              categories.find(
                                (category) => clientCategoryId === category.id
                              )?.name
                            }{" "}
                          </div>
                          {loadImages[product.id] ? (
                            <>
                              {[...categories]
                                .filter((item) => item.id !== 7)
                                .map((category: any) => {
                                  const imageUrl = getProductImageByCategory(
                                    product,
                                    category.id
                                  )?.attachment?.url;
                                  if (!imageUrl) return null;

                                  if (
                                    videosCategoriesIds?.includes(category.id)
                                  ) {
                                    return (
                                      <div
                                        key={`key-${imageUrl}-frame`}
                                        className="flex flex-col  w-full justify-center align-center"
                                      >
                                        <div
                                          id={`${product.id}-${imageUrl}`}
                                          className="mx-auto my-5 gap-2 flex flex-col items-center"
                                        >
                                          <video
                                            controls
                                            className="max-h-[500px] max-w-full"
                                            src={imageUrl}
                                          />
                                        </div>
                                      </div>
                                    );
                                  }
                                  return (
                                    <div
                                      className="mx-auto my-5 gap-2 flex flex-col items-center"
                                      key={`${product.id}-${category.id}`}
                                    >
                                      <Image
                                        className="max-h-[500px]"
                                        src={imageUrl}
                                        about="teste"
                                      />
                                      {category.name}
                                    </div>
                                  );
                                })}
                            </>
                          ) : (
                            <Button
                              type="dashed"
                              size="small"
                              className="w-48 mx-auto mb-5"
                              onClick={() => {
                                setLoadImages({
                                  [product.id]: true,
                                });
                                setTimeout(() => {
                                  scrollToElement(
                                    `${product.id}-${
                                      categories[categories.length - 1].id
                                    }`
                                  );
                                }, 100);
                              }}
                            >
                              Carregar Todas Fotos
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              {ticket?.groups.map((group) => (
                <div
                  key={group.id}
                  className="flex flex-col w-full justify-center align-center"
                >
                  <div className="mx-auto  flex flex-col items-center">
                    <Title level={5}> {group.name}</Title>
                  </div>
                  <div
                    key={`key-${
                      getClientImage(group.fieldReportProductAttachment)?.id
                    }-frame`}
                    className="flex flex-col  w-full justify-center align-center"
                  >
                    <div
                      id={`${group.id}-${
                        getClientImage(group.fieldReportProductAttachment)?.id
                      }`}
                      className="mx-auto my-5 gap-2 flex flex-col items-center"
                    >
                      <Image
                        className="max-h-[500px]"
                        src={
                          getClientImage(group.fieldReportProductAttachment)
                            ?.attachment.url
                        }
                      />
                      {
                        categories?.find(
                          (category) =>
                            category.id ===
                            getClientImage(group.fieldReportProductAttachment)
                              ?.productImageCategoryId
                        )?.name
                      }
                    </div>
                  </div>
                  {loadImages[group.id] ? (
                    <>
                      {group.fieldReportProducts.map((product) => (
                        <div
                          className="border border-black p-5"
                          key={`key-${product.id}-frame`}
                        >
                          <p className="text-center">
                            {product.product.description}
                          </p>
                          {product.productAttachment.map(
                            (attachment: any, index: number) => {
                              return (
                                <div
                                  key={`key-${attachment.id}-frame`}
                                  className="flex flex-col  w-full justify-center align-center"
                                >
                                  <div
                                    id={`${group.id}-${attachment.id}`}
                                    className="mx-auto my-5 gap-2 flex flex-col items-center"
                                  >
                                    <Image
                                      className="max-h-[500px]"
                                      src={attachment.attachment.url}
                                    />
                                    {
                                      categories?.find(
                                        (category) =>
                                          category.id ===
                                          attachment.productImageCategoryId
                                      ).name
                                    }
                                  </div>
                                </div>
                              );
                            }
                          )}
                        </div>
                      ))}
                      {getImagesWithoutClient(
                        group.fieldReportProductAttachment
                      ).map((attachment) => (
                        <div
                          key={`key-${attachment.id}-frame`}
                          className="flex flex-col  w-full justify-center align-center"
                        >
                          <div
                            id={`${group.id}-${attachment.id}`}
                            className="mx-auto my-5 gap-2 flex flex-col items-center"
                          >
                            <Image
                              className="max-h-[500px]"
                              src={attachment.attachment.url}
                            />
                            {
                              categories?.find(
                                (category) =>
                                  category.id ===
                                  attachment.productImageCategoryId
                              ).name
                            }
                          </div>
                        </div>
                      ))}
                      {getVideos(group.fieldReportProductAttachment).map(
                        (video) => (
                          <div
                            key={`key-${video.id}-frame`}
                            className="flex flex-col  w-full justify-center align-center"
                          >
                            <div
                              id={`${group.id}-${video.attachment.id}`}
                              className="mx-auto my-5 gap-2 flex flex-col items-center"
                            >
                              <video
                                controls
                                className="max-h-[500px] max-w-full"
                                src={video.attachment.url}
                              />
                            </div>
                          </div>
                        )
                      )}
                    </>
                  ) : (
                    <Button
                      type="dashed"
                      size="small"
                      className="w-48 mx-auto mb-5"
                      onClick={() => {
                        setLoadImages({
                          [group.id]: true,
                        });
                      }}
                    >
                      Carregar Todas Fotos
                    </Button>
                  )}
                </div>
              ))}
            </>
          )}
        </>
        <PartsRequestList PartsRequest={ticket?.PartsRequest ?? []} />
        <div className="flex justify-center gap-8">
          <Button color="danger" onClick={() => setShowConfirmationModal(true)}>
            Rejeitar
          </Button>

          <Popconfirm
            zIndex={5000}
            onConfirm={() => sendToSchedule()}
            title={
              <div>
                <div>Digite o Motivo do Reagendamento:</div>
                <Input
                  type="text"
                  onChange={(e) => setSendToScheduleComment(e.target.value)}
                />
              </div>
            }
            okText="Sim"
            okButtonProps={{ disabled: !!!sendToScheduleComment }}
            cancelText="Cancelar"
          >
            <Button type="dashed">Enviar Para Agendamento</Button>
          </Popconfirm>
          {!!!ticket?.cancellationReason && (
            <Button onClick={() => approveTicket()} type="primary">
              Aprovar
            </Button>
          )}
        </div>
      </Modal>
      <Button size="small" onClick={() => setIsModalOpen(true)}>
        {ticket?.cancellationReason
          ? "Analisar Cancelamento"
          : "Analisar Relatorio"}
      </Button>
    </>
  );
};
